// Layer 4 (UI) — the Pareto tab's lead region: every cause drawn twice,
// equipment hours to the left and US$ of lost mill production to the right
// (locked comp `downtime.html`, B's butterfly folded into A). Every figure
// comes from HoursDollars.transform.ts; nothing is typed here.

import { formatCurrency } from '@/versions/v1/modules/capacity/Scene.transform';
import { cn } from '@/shared/cn';
import { ActorChip } from '../advisory/PipelineStrip';
import type { DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import { formatHours } from '../maintenance/Downtime.transform';
import { DowntimeCard } from './DowntimeCard';
import { hoursVersusDollars } from './HoursDollars.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

/** Longest bar as a share of its half, leaving room for the figure beside it. */
const BAR_REACH = 0.72;
const HEAD = 'border-b border-divider pb-2 text-xs font-semibold uppercase tracking-[0.07em] text-muted';
const CELL = 'flex min-w-0 items-center gap-2 border-b border-divider py-2';

export function HoursDollars({ rows, windowLabel }: { rows: readonly DowntimeEvent[]; windowLabel: string }) {
  const lines = hoursVersusDollars(rows);
  return (
    <DowntimeCard
      title="Where the hours go, and where the money goes"
      meta={`${windowLabel} · equipment hours left · ${CURRENCY_GLYPH} lost mill production right`}
      actions={<ActorChip actor="Engine" />}
    >
      <div data-hours-dollars className="grid grid-cols-[minmax(0,1fr)_12.5rem_minmax(0,1fr)] items-center gap-x-3.5 text-sm tabular-nums">
        <span className={cn(HEAD, 'text-right')}>Equipment hours · events</span>
        <span className={cn(HEAD, 'text-center')}>Cause</span>
        <span className={HEAD}>{CURRENCY_GLYPH} lost mill production · share</span>
        {lines.map((line, index) => {
          const last = index === lines.length - 1 ? 'border-b-0' : '';
          return (
            <div key={line.causeCategory} data-hours-dollars-row={line.causeCategory} className="contents">
              <span className={cn(CELL, 'justify-end', last)}>
                <span className="whitespace-nowrap font-semibold text-ink-primary">
                  {formatHours(line.hours)} <span className="text-xs font-normal text-muted">· {line.eventCount} ev</span>
                </span>
                <span
                  className="h-3 shrink-0 rounded-[3px] bg-[color-mix(in_srgb,var(--ink-secondary)_16%,transparent)]"
                  style={{ width: `${line.hoursRatio * BAR_REACH * 100}%` }}
                />
              </span>
              <span className={cn('truncate border-b border-divider py-2 text-center font-medium text-ink-primary', last)}>
                {line.label}
              </span>
              <span className={cn(CELL, last)}>
                {line.dollars > 0 ? (
                  <>
                    <span
                      className="h-3 shrink-0 rounded-[3px]"
                      style={{
                        width: `${Math.max(0.6, line.dollarsRatio * BAR_REACH * 100)}%`,
                        backgroundColor: index === 0 ? 'var(--color-accent-500)' : 'var(--ink-primary)',
                      }}
                    />
                    <span className="whitespace-nowrap font-semibold text-ink-primary">
                      {formatCurrency(line.dollars)} <span className="text-xs font-normal text-muted">· {Math.round(line.sharePct)}%</span>
                    </span>
                  </>
                ) : (
                  <span className="whitespace-nowrap font-medium text-muted">
                    {formatCurrency(0)} <span className="text-xs font-normal">· buffered or off the mill path</span>
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </DowntimeCard>
  );
}
